import { forwardRef } from 'react';
import styles from './Button.module.css';

/**
 * Unified button component for Dermalink.
 *
 * variant  — visual style (maps to the global .btn-* classes in tokens.css)
 *   'primary'  salmon pill — main action
 *   'ghost'    white/subtle pill — secondary / back navigation
 *   'danger'   red pill — destructive actions
 *
 * size — 'md' (default) | 'lg' — matches btn-primary vs btn-primary-lg sizing
 *
 * icon — ReactNode (SVG) — when provided the button shows label text on
 *   desktop and only the icon on mobile (≤ 640 px). Use icons from icons.tsx.
 *
 * type defaults to 'button' so buttons inside a <form> never accidentally
 * submit — use type="submit" explicitly for submit buttons.
 */

type Variant = 'primary' | 'ghost' | 'danger';
type Size    = 'md' | 'lg';

const VARIANT_CLASS: Record<Variant, Record<Size, string>> = {
  primary: { md: 'btn btn-primary',    lg: 'btn btn-primary-lg' },
  ghost:   { md: 'btn btn-ghost',      lg: 'btn btn-ghost-lg'   },
  danger:  { md: 'btn btn-danger',     lg: 'btn btn-danger'     },
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** SVG icon node. When supplied the button shows text on desktop, icon on mobile. */
  icon?: React.ReactNode;
  /** Shows a spinner in place of the label/icon and disables the button. */
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant  = 'primary',
    size     = 'md',
    icon,
    isLoading = false,
    type     = 'button',
    className,
    children,
    disabled,
    ...rest
  },
  ref,
) {
  const baseClass = VARIANT_CLASS[variant][size];
  const cls = className ? `${baseClass} ${className}` : baseClass;
  const hasTextChildren = children !== undefined && children !== null && children !== false;

  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || isLoading} aria-busy={isLoading || undefined} {...rest}>
      {isLoading ? (
        <span className={styles.spinner} aria-hidden="true" />
      ) : icon ? (
        hasTextChildren ? (
          <>
            <span className={styles.btnText}>{children}</span>
            <span className={styles.btnIcon}>{icon}</span>
          </>
        ) : (
          <span className={styles.btnIcon} style={{ display: 'inline-flex', alignItems: 'center' }}>{icon}</span>
        )
      ) : (
        children
      )}
    </button>
  );
});
